import { planInsertPoint } from '@fit-editor/fit-core'
import type { Activity, Operation } from '@fit-editor/fit-core'

import type { GpsPoint } from './geo'

/**
 * Pure helpers behind the map's point-editing mode (drag / multi-select /
 * insert / delete GPS points). Everything here is free of MapLibre and
 * React: screen positions come in as plain `{ x, y }` (callers project
 * with `map.project`, which accounts for pitch and 3D terrain), and the
 * functions only decide *what* to do -- which handles to show, which
 * point is under the cursor, which `fit-core` operation(s) an action
 * becomes. The operations themselves are applied through the store, so
 * every edit joins the global undo history.
 *
 * Points are identified by index into the `GpsPoint[]` array
 * (`extractGpsPoints`) for screen work, and by exact record timestamp for
 * the operations, which address records by timestamp. Points without a
 * timestamp can't be addressed by any operation and are never editable.
 */

export interface ScreenPoint {
  x: number
  y: number
}

export interface ScreenBox {
  x0: number
  y0: number
  x1: number
  y1: number
}

/** Upper bound on drawn handles; above this the user is asked to zoom in. */
export const MAX_VERTEX_HANDLES = 1500
/** Median on-screen spacing (px) between consecutive points below which handles are too dense to grab. */
export const MIN_HANDLE_SPACING_PX = 6

export type HandlePlan =
  { status: 'visible'; indices: number[] } | { status: 'zoom-in'; reason: 'too-many' | 'too-dense' }

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length === 0) return Number.POSITIVE_INFINITY
  return sorted.length % 2 === 1
    ? (sorted[mid] as number)
    : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2
}

function distance(a: ScreenPoint, b: ScreenPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/**
 * Which points get a draggable handle: the editable (timestamped) points
 * inside the viewport, plus each one's immediate neighbours so a segment
 * crossing the viewport edge can still be grabbed/split. Handles are
 * hidden entirely (`zoom-in`) when there are too many of them or they are
 * packed too tightly to be individually grabbable -- only the cheap
 * `isInView` test runs for every point, `project` only for in-view ones.
 */
export function planVertexHandles(
  points: readonly GpsPoint[],
  isInView: (point: GpsPoint) => boolean,
  project: (point: GpsPoint) => ScreenPoint,
  limits: { maxHandles?: number; minSpacingPx?: number } = {},
): HandlePlan {
  const maxHandles = limits.maxHandles ?? MAX_VERTEX_HANDLES
  const minSpacingPx = limits.minSpacingPx ?? MIN_HANDLE_SPACING_PX

  const inView: number[] = []
  for (let i = 0; i < points.length; i++) {
    const p = points[i] as GpsPoint
    if (p.timestampMs == null || !isInView(p)) continue
    inView.push(i)
    if (inView.length > maxHandles) return { status: 'zoom-in', reason: 'too-many' }
  }

  const gaps: number[] = []
  for (let k = 1; k < inView.length; k++) {
    const i = inView[k] as number
    const prev = inView[k - 1] as number
    if (prev !== i - 1) continue
    gaps.push(distance(project(points[prev] as GpsPoint), project(points[i] as GpsPoint)))
  }
  if (gaps.length > 0 && median(gaps) < minSpacingPx) {
    return { status: 'zoom-in', reason: 'too-dense' }
  }

  const indices = new Set<number>()
  for (const i of inView) {
    indices.add(i)
    for (const n of [i - 1, i + 1]) {
      if (points[n]?.timestampMs != null) indices.add(n)
    }
  }
  return { status: 'visible', indices: [...indices].sort((a, b) => a - b) }
}

export interface SegmentHit {
  /** Index (into the points array) of the segment's earlier vertex; the later one is `fromIndex + 1`. */
  fromIndex: number
  /** Position along the segment, 0 (at `fromIndex`) to 1 (at `fromIndex + 1`). */
  t: number
  /** Screen distance from the cursor to the segment, px. */
  distancePx: number
}

/** Closest point to `p` on segment `a`-`b`, as a fraction `t` and a distance. */
export function projectOntoSegment(
  p: ScreenPoint,
  a: ScreenPoint,
  b: ScreenPoint,
): { t: number; distancePx: number } {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lengthSq = dx * dx + dy * dy
  const t =
    lengthSq === 0 ? 0 : Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq))
  return { t, distancePx: distance(p, { x: a.x + dx * t, y: a.y + dy * t }) }
}

/**
 * The route segment nearest `cursor` among segments whose BOTH vertices
 * have handles (`handles` maps point index -> screen position), within
 * `maxDistancePx`. Returns `null` when nothing is close enough, or when
 * the cursor is within `vertexClearancePx` of either vertex -- there the
 * user is grabbing the vertex, not splitting the segment.
 */
export function nearestSegment(
  cursor: ScreenPoint,
  handles: ReadonlyMap<number, ScreenPoint>,
  maxDistancePx = 10,
  vertexClearancePx = 9,
): SegmentHit | null {
  let best: SegmentHit | null = null
  for (const [index, a] of handles) {
    const b = handles.get(index + 1)
    if (b == null) continue
    const { t, distancePx } = projectOntoSegment(cursor, a, b)
    if (distancePx > maxDistancePx) continue
    if (best == null || distancePx < best.distancePx) best = { fromIndex: index, t, distancePx }
  }
  if (best == null) return null
  const a = handles.get(best.fromIndex) as ScreenPoint
  const b = handles.get(best.fromIndex + 1) as ScreenPoint
  if (distance(cursor, a) < vertexClearancePx || distance(cursor, b) < vertexClearancePx) {
    return null
  }
  return best
}

/** Linear lat/lon interpolation between two points (fine at handle-visible zoom levels, where segments are short). */
export function interpolateLatLon(
  a: Pick<GpsPoint, 'lat' | 'lon'>,
  b: Pick<GpsPoint, 'lat' | 'lon'>,
  t: number,
): { lat: number; lon: number } {
  return { lat: a.lat + (b.lat - a.lat) * t, lon: a.lon + (b.lon - a.lon) * t }
}

/** Indices of the handles whose screen position lies inside `box` (corners in any order). */
export function handlesInBox(handles: ReadonlyMap<number, ScreenPoint>, box: ScreenBox): number[] {
  const minX = Math.min(box.x0, box.x1)
  const maxX = Math.max(box.x0, box.x1)
  const minY = Math.min(box.y0, box.y1)
  const maxY = Math.max(box.y0, box.y1)
  const hits: number[] = []
  for (const [index, p] of handles) {
    if (p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY) hits.push(index)
  }
  return hits.sort((a, b) => a - b)
}

function clampLat(lat: number): number {
  return Math.min(90, Math.max(-90, lat))
}

function wrapLon(lon: number): number {
  if (lon >= -180 && lon <= 180) return lon
  return ((((lon + 180) % 360) + 360) % 360) - 180
}

/**
 * New positions for the points being dragged: every point in `indices`
 * moves by the same lat/lon offset as the cursor moved from `from` to
 * `to`. Latitude is clamped to [-90, 90] and longitude wrapped to
 * [-180, 180], the ranges `moveGpsPoints` accepts.
 */
export function dragPositions(
  points: readonly GpsPoint[],
  indices: readonly number[],
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
): Map<number, { lat: number; lon: number }> {
  const dLat = to.lat - from.lat
  const dLon = to.lon - from.lon
  const result = new Map<number, { lat: number; lon: number }>()
  for (const i of indices) {
    const p = points[i]
    if (p == null) continue
    result.set(i, { lat: clampLat(p.lat + dLat), lon: wrapLon(p.lon + dLon) })
  }
  return result
}

/** `points` with `overrides` applied -- the live drag preview of the route. */
export function withOverrides(
  points: readonly GpsPoint[],
  overrides: ReadonlyMap<number, { lat: number; lon: number }>,
): GpsPoint[] {
  return points.map((p, i) => {
    const o = overrides.get(i)
    return o == null ? p : { ...p, lat: o.lat, lon: o.lon }
  })
}

/** One `moveGpsPoints` operation (one undo step) for every dragged point, or `null` if nothing addressable moved. */
export function buildMoveOperation(
  points: readonly GpsPoint[],
  positions: ReadonlyMap<number, { lat: number; lon: number }>,
): Operation | null {
  const moves: { timestampMs: number; lat: number; lon: number }[] = []
  for (const [i, pos] of positions) {
    const t = points[i]?.timestampMs
    if (t == null) continue
    moves.push({ timestampMs: t, lat: pos.lat, lon: pos.lon })
  }
  return moves.length === 0 ? null : { type: 'moveGpsPoints', moves }
}

export type InsertAction =
  { ok: true; operation: Operation; timestampMs: number } | { ok: false; reason: string }

/**
 * The `insertPoint` operation for a click on the segment starting at the
 * record at `afterTimestampMs`, or -- when `fit-core`'s `planInsertPoint`
 * says no record can go there (FIT timestamps are whole seconds, so the
 * neighbours must be at least 2 s apart) -- the reason, for display.
 */
export function buildInsertAction(
  activity: Activity,
  afterTimestampMs: number,
  position: { lat: number; lon: number },
): InsertAction {
  const plan = planInsertPoint(activity, afterTimestampMs)
  if (!plan.ok) return { ok: false, reason: plan.reason }
  return {
    ok: true,
    operation: { type: 'insertPoint', afterTimestampMs, lat: position.lat, lon: position.lon },
    timestampMs: plan.timestampMs,
  }
}

/**
 * `removeGpsOnly` operations clearing GPS from exactly the selected
 * points. `removeGpsOnly` works on an inclusive time range and strips GPS
 * from EVERY record inside it, so selected points are grouped into runs
 * that are consecutive among all timestamped GPS points: a run's range
 * then contains no unselected GPS point (records in between that have no
 * GPS are unaffected by the operation anyway). A contiguous selection is
 * one operation (one undo step); a selection with gaps needs one per run.
 */
export function buildRemoveGpsOperations(
  points: readonly GpsPoint[],
  selectedTimestampsMs: readonly number[],
): Operation[] {
  const selected = new Set(selectedTimestampsMs)
  const times = [
    ...new Set(points.map((p) => p.timestampMs).filter((t): t is number => t != null)),
  ].sort((a, b) => a - b)
  const operations: Operation[] = []
  let runStart: number | null = null
  let runEnd: number | null = null
  const flush = () => {
    if (runStart != null && runEnd != null) {
      operations.push({ type: 'removeGpsOnly', start: new Date(runStart), end: new Date(runEnd) })
    }
    runStart = null
    runEnd = null
  }
  for (const t of times) {
    if (selected.has(t)) {
      runStart ??= t
      runEnd = t
    } else {
      flush()
    }
  }
  flush()
  return operations
}

/** One `deleteRecords` operation (one undo step) for every selected point, or `null` for an empty selection. */
export function buildDeleteRecordsOperation(
  selectedTimestampsMs: readonly number[],
): Operation | null {
  const timestampsMs = [...new Set(selectedTimestampsMs)].sort((a, b) => a - b)
  return timestampsMs.length === 0 ? null : { type: 'deleteRecords', timestampsMs }
}

/** Shift+click: adds `timestampMs` to the selection, or removes it if already selected. */
export function toggleInSelection(selection: readonly number[], timestampMs: number): number[] {
  return selection.includes(timestampMs)
    ? selection.filter((t) => t !== timestampMs)
    : [...selection, timestampMs].sort((a, b) => a - b)
}

/**
 * Every point from `anchorMs` to `targetMs` inclusive, in track order
 * (whichever comes first) -- the Shift+click range, like a file list. A
 * missing anchor (never set, or its point was since deleted) gives just
 * the target, so Shift+click without an anchor behaves like a plain click.
 */
export function timestampsBetween(
  points: readonly GpsPoint[],
  anchorMs: number | null,
  targetMs: number,
): number[] {
  const to = points.findIndex((p) => p.timestampMs === targetMs)
  const from = anchorMs == null ? -1 : points.findIndex((p) => p.timestampMs === anchorMs)
  if (to === -1) return []
  if (from === -1) return [targetMs]
  return points
    .slice(Math.min(from, to), Math.max(from, to) + 1)
    .flatMap((p) => (p.timestampMs == null ? [] : [p.timestampMs]))
}

/** Union of `selection` and `added`, sorted, without duplicates. */
export function addToSelection(selection: readonly number[], added: readonly number[]): number[] {
  return [...new Set([...selection, ...added])].sort((a, b) => a - b)
}

/** The selection restricted to timestamps that still have a GPS point (after an undo/delete/etc.). */
export function pruneSelection(
  selection: readonly number[],
  points: readonly GpsPoint[],
): number[] {
  if (selection.length === 0) return selection as number[]
  const existing = new Set(points.map((p) => p.timestampMs))
  const pruned = selection.filter((t) => existing.has(t))
  return pruned.length === selection.length ? (selection as number[]) : pruned
}

/** Human-readable count, e.g. "1 point" / "3 points". */
export function pointCount(n: number): string {
  return n === 1 ? '1 point' : `${n} points`
}
